import { createContext, useContext, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { TRACKING_CONFIG } from "@/tracking/config";
import { trackEvent, trackIntroMeetingBooked } from "@/tracking/analytics";
import { stopScroll, startScroll } from "@/lib/scroll";

const BookingContext = createContext(() => {});
export const useBooking = () => useContext(BookingContext);

// Single conversion path: every audit CTA opens this one Calendly dialog.
// A confirmed booking redirects to /thank-you; the visitor never lands on
// a Calendly confirmation page.
export const BookingProvider = ({ children }) => {
    const [open, setOpen] = useState(false);
    const hostRef = useRef(null);
    const calendlyReady =
        TRACKING_CONFIG.calendly.enabled && !!TRACKING_CONFIG.calendly.url;

    useEffect(() => {
        const onMessage = (e) => {
            if (e.origin !== "https://calendly.com") return;
            if (e.data && e.data.event === "calendly.event_scheduled") {
                trackIntroMeetingBooked({
                    calendly_event_uri: e.data.payload?.event?.uri,
                });
                window.location.assign("/thank-you");
            }
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, []);

    useEffect(() => {
        if (!open || !hostRef.current || !calendlyReady) return;
        trackEvent("booking_widget_opened");
        if (!document.querySelector("link[data-calendly-css]")) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href =
                "https://assets.calendly.com/assets/external/widget.css";
            link.dataset.calendlyCss = "true";
            document.head.appendChild(link);
        }
        const init = () =>
            window.Calendly.initInlineWidget({
                url: TRACKING_CONFIG.calendly.url,
                parentElement: hostRef.current,
            });
        if (window.Calendly) {
            init();
        } else {
            const s = document.createElement("script");
            s.src = "https://assets.calendly.com/assets/external/widget.js";
            s.async = true;
            s.onload = init;
            document.body.appendChild(s);
        }
    }, [open, calendlyReady]);

    useEffect(() => {
        if (open) {
            stopScroll();
        } else {
            startScroll();
        }
        return () => startScroll();
    }, [open]);

    return (
        <BookingContext.Provider value={() => setOpen(true)}>
            {children}
            {open && (
                <div
                    className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm p-4 md:p-10"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Book your free audit call"
                    data-testid="booking-dialog"
                    onClick={() => setOpen(false)}
                >
                    <div
                        className="relative mx-auto h-full max-w-4xl bg-white"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            data-testid="booking-close-button"
                            onClick={() => setOpen(false)}
                            aria-label="Close booking dialog"
                            className="absolute -top-9 right-0 inline-flex items-center gap-1.5 text-sm text-white hover:text-brass-400"
                        >
                            Close <X className="h-4 w-4" />
                        </button>
                        <div
                            ref={hostRef}
                            className="h-full min-h-[600px]"
                            title="Calendly scheduling"
                        />
                    </div>
                </div>
            )}
        </BookingContext.Provider>
    );
};

export default BookingProvider;
